// EXTERNAL
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
// COMPONENTS
import * as ComponentLibrary from "@picnicmedia/masanatemples123";
import { PCSelect } from "@picnicmedia/masanatemples123";
// LIBS
import { s3_media_upload } from "../../../../libs/amplify";
import { ingest_package } from "../../../../libs/node";
import { logger } from "../../../../libs/logger";
import { Google_Analytics_Event } from "../../../../libs/google-analytics";
// CONTEXT
import { useNotificationDispatch } from "../../../../contexts/notification.context";
// HELPERS
import create_readable_id from "../../../../helpers/create_readable_id";
// TYPES
import { PackageUploadModalProps } from "../types";

interface AdvertiserOption {
  label: string;
  value: string;
  id: string;
}

// Ad names flow straight into S3 keys and Adform banner names, so mirror the template
// flow's whitelist (see validate_ad_creator_form in handleCreativeSave).
const AD_NAME_PATTERN = /^[A-Za-z0-9-_ ]*$/;

/**
 * JH (29-07-26)
 * Upload a bring-your-own HTML5 ad package into the Library.
 *
 * Flow: pick a .zip → upload it to S3 (same Storage.put path as asset uploads) → POST its key to
 * /ingest-package, which validates the package, unpacks it for preview, keeps the author's zip as
 * export-adform.zip, and writes the Creative record. Width/height are left to the server to read
 * from manifest.json; format defaults to "custom" server-side. On a validation failure the endpoint
 * returns 422 with { problems, warnings } — we render the problems inline and keep the modal open.
 */
export function PackageUploadModal({ open, setOpen, user, advertisersData, presetAdvertiser, refreshCreatives }: PackageUploadModalProps) {
  const notificationDispatch = useNotificationDispatch();

  const [file, setFile] = useState<File | null>(null);
  const [advertiser, setAdvertiser] = useState<AdvertiserOption | null>(null);
  const [adName, setAdName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [problems, setProblems] = useState<string[]>([]);
  const [dragging, setDragging] = useState(false);

  // Dedupe advertisers by name (the same brand can span many creatives) and sort alphabetically —
  // matches AdvertiserDropdown so the two entry points feel identical.
  const advertiserOptions: AdvertiserOption[] = Object.values(
    advertisersData.reduce((acc: Record<string, AdvertiserOption>, { advertiserId, advertiserName }) => {
      if (advertiserName && !acc[advertiserName]) acc[advertiserName] = { label: advertiserName, value: advertiserName, id: advertiserId };
      return acc;
    }, {}),
  ).sort((a, b) => a.value.localeCompare(b.value));

  const resetForm = () => {
    setFile(null);
    setAdvertiser(presetAdvertiser ? { label: presetAdvertiser.advertiserName, value: presetAdvertiser.advertiserName, id: presetAdvertiser.advertiserId } : null);
    setAdName("");
    setProblems([]);
    setSubmitting(false);
    setDragging(false);
  };

  // Re-seed the preset advertiser whenever the modal (re)opens.
  useEffect(() => {
    if (open) {
      setProblems([]);
      setAdvertiser(presetAdvertiser ? { label: presetAdvertiser.advertiserName, value: presetAdvertiser.advertiserName, id: presetAdvertiser.advertiserId } : null);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = () => {
    if (submitting) return; // don't let the user bail mid-upload
    resetForm();
    setOpen(false);
  };

  // Selecting an existing advertiser reuses its id; typing a new one mints a readable id, exactly as
  // AdvertiserDropdown does, so a package can seed a brand-new brand.
  const handleAdvertiserChange = (selected: AdvertiserOption | null) => setAdvertiser(selected);
  const handleAdvertiserCreate = (input: string) => {
    const existing = advertiserOptions.find((o) => o.value.toLowerCase().replace(/\s/g, "") === input.toLowerCase().replace(/\s/g, ""));
    setAdvertiser(existing || { label: input, value: input, id: create_readable_id() });
  };

  const handleFileChange = (files: FileList | null) => {
    setProblems([]);
    const picked = files && files[0];
    if (!picked) return;
    if (!/\.zip$/i.test(picked.name)) {
      setProblems([`"${picked.name}" is not a .zip — export your package as a zip and try again.`]);
      return;
    }
    setFile(picked);
  };

  const validate = (): string[] => {
    const issues: string[] = [];
    if (!file) issues.push("Choose a package .zip to upload.");
    if (!advertiser) issues.push("Select or create an advertiser.");
    if (!adName.trim()) issues.push("Enter an ad name.");
    else if (!AD_NAME_PATTERN.test(adName)) issues.push("Ad name can only contain letters, numbers, spaces, hyphens and underscores.");
    return issues;
  };

  const handleSubmit = async () => {
    const issues = validate();
    if (issues.length) {
      setProblems(issues);
      return;
    }
    setProblems([]);
    setSubmitting(true);
    const creativeId = uuidv4();
    try {
      // 1) Upload the zip to the creative's S3 folder. customPrefix "public" means the stored key is
      //    public/{teamId}/{creativeId}/source-upload.zip — which is what the ingest guard expects.
      await s3_media_upload({ fileKey: "source-upload.zip", mimeType: "application/zip", body: file, teamId: user.teamId, creativeId });

      // 2) Hand the key to the ingest lambda. Omitting width/height lets it read them from manifest.json.
      const result = await ingest_package({
        teamId: user.teamId,
        advertiserId: advertiser!.id,
        advertiserName: advertiser!.value,
        adName: adName.trim(),
        authorId: user.id,
        authorEmail: user.email,
        sourceZipKey: `public/${user.teamId}/${creativeId}/source-upload.zip`,
        creativeId,
      });

      Google_Analytics_Event({ name: "package_uploaded", category: "ad_creative", action: "ingest", label: creativeId });

      const warnings: string[] = result?.warnings || [];
      notificationDispatch({
        type: "ADD",
        payload: {
          header: "Package uploaded",
          message: warnings.length ? `Added to your library with ${warnings.length} warning${warnings.length > 1 ? "s" : ""}: ${warnings.join("; ")}` : "Added to your library.",
          type: "success",
          autoDismiss: !warnings.length,
        },
      });

      resetForm();
      setOpen(false);
      await refreshCreatives(creativeId);
    } catch (error: any) {
      const payload = error?.response?.data?.payload;
      // 422 validation failures carry a structured { message, problems, warnings }; everything else
      // (413 too big, 403 wrong folder, network) surfaces as a single message.
      if (payload && Array.isArray(payload.problems)) {
        setProblems(payload.problems);
      } else {
        const message = (typeof payload === "string" && payload) || error?.message || "Upload failed. Please try again.";
        setProblems([message]);
      }
      logger({ error, user, context: { origin: "PackageUploadModal_handleSubmit" } });
      Google_Analytics_Event({ name: "error", category: `${error?.message}`, action: "PackageUploadModal_handleSubmit", label: user.email });
    } finally {
      setSubmitting(false);
    }
  };

  // No fixed height on PCDialogueBox — it applies height as a CSS class that clips overflow, so we let
  // it size to content instead (the drop zone below is deliberately compact).
  return (
    <ComponentLibrary.PCDialogueBox open={open} width={"width-14"} onClose={close} hideInitialFocus={true}>
      <div className="mh3 platform-font">
        <ComponentLibrary.PCHeader text={"Upload package"} />
        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={2} />
        <div className="mid-gray f6 platform-font">Upload a pre-built HTML5 ad package (.zip). We'll validate it, add it to your library, and keep your zip for tagging.</div>
        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />

        {/* Self-contained drop zone: a <label> wrapping a hidden file input, so a click or a drop both
            select the zip and we get real selected-file feedback (PCDragAndDrop gives neither). */}
        <label
          htmlFor={"package-zip-input"}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFileChange(e.dataTransfer.files);
          }}
          className={"db br3 tc pointer platform-font"}
          style={{ padding: "22px", border: `2px dashed ${dragging ? "#d6336c" : "#d9d9d9"}`, backgroundColor: dragging ? "#fdf0f5" : "#fafafa", transition: "background-color .15s, border-color .15s" }}>
          <input id={"package-zip-input"} type={"file"} accept={".zip,application/zip"} style={{ display: "none" }} onChange={(e) => handleFileChange(e.target.files)} />
          {file ? (
            <div className="flex items-center justify-center">
              <ComponentLibrary.PCIconPicker icon={"file-zipper"} size={"lg"} />
              <span className="ml3 fw6 f6 dark-gray">{file.name}</span>
              <span className="ml2 f6 mid-gray">({(file.size / 1048576).toFixed(2)} MB)</span>
            </div>
          ) : (
            <div>
              <div className="mid-gray f6">
                Drag &amp; drop your package <strong>.zip</strong> here, or
              </div>
              <div className="mt2">
                <span className="dib background-color-primary-3 white ph3 pv2 br2 fw6 f6">Browse</span>
              </div>
            </div>
          )}
        </label>
        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />

        <PCSelect
          handleCreate={handleAdvertiserCreate}
          handleChange={handleAdvertiserChange}
          options={[{ label: "Select an advertiser or type to create", options: advertiserOptions }]}
          placeholder={"Select an advertiser"}
          selectedOption={advertiser}
          label={"Advertiser"}
        />
        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />

        <ComponentLibrary.PCInput label={"Ad name"} placeholder={"Ad name"} type={"text"} value={adName} handleChange={(e: any) => setAdName(e.target.value)} />

        {problems.length > 0 && (
          <>
            <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
            <div className="pa3 br2 platform-font" style={{ backgroundColor: "#fdecec" }}>
              <div className="f6 fw6" style={{ color: "#c0392b" }}>
                Package couldn't be uploaded:
              </div>
              <ul className="f6 mt2 mb0" style={{ color: "#c0392b", paddingLeft: "18px" }}>
                {problems.map((problem, i) => (
                  <li key={i}>{problem}</li>
                ))}
              </ul>
            </div>
          </>
        )}

        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={5} />
        <div className="flex justify-left items-center">
          <ComponentLibrary.PCActionButton text={submitting ? "Uploading…" : "Upload package"} textColor={"white"} color={"background-color-primary-3"} isDisabled={submitting} clickHandler={handleSubmit} />
          <ComponentLibrary.PCSpacer type={"margin"} position={"left"} size={3} />
          <ComponentLibrary.PCActionButton text="Cancel" textColor={"color-text-5"} color={""} isDisabled={submitting} clickHandler={close} />
        </div>
        <ComponentLibrary.PCSpacer type={"margin"} position={"top"} size={4} />
      </div>
    </ComponentLibrary.PCDialogueBox>
  );
}
